import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  FileText, Search, Trash2, RefreshCw, Loader2,
  Database, AlertCircle,
  Filter, FolderOpen, Settings, ExternalLink, MessageSquare, HardDrive
} from 'lucide-react'
import { useTenant } from '@/hooks/useTenant'
import { getAuthToken } from '@/hooks/useAuth'
import { KBChatInterface, KBDocumentExplorer } from '@/components/knowledge-base'
import { checkKBIntegration, API_BASE } from '@/services/api'

type TabType = 'documents' | 'chat'

interface KBDocument {
  document_id: string
  name: string
  description: string
  file_type: string
  category: string
  status: string
  size_bytes: number
  created_at: string
  created_by: string
  is_active: boolean
}

interface KBStats {
  total_documents: number
  total_chunks: number
  total_size_bytes: number
  total_size_mb: number
  by_category: Record<string, number>
  by_file_type: Record<string, number>
}

interface Category {
  value: string
  label: string
  description: string
}

// Module-level cache to persist data across navigation
let _cachedKBDocs: KBDocument[] | null = null
let _cachedKBStats: KBStats | null = null
let _cachedKBCategories: Category[] | null = null

export default function KnowledgeBasePage() {
  const { tenantId, isLoading: tenantLoading } = useTenant()
  const [documents, setDocuments] = useState<KBDocument[]>(_cachedKBDocs || [])
  const [stats, setStats] = useState<KBStats | null>(_cachedKBStats)
  const [categories, setCategories] = useState<Category[]>(_cachedKBCategories || [])
  const [isLoading, setIsLoading] = useState(!_cachedKBDocs)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string>('')

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('documents')

  // OpenAI Integration state
  const [openaiConnected, setOpenaiConnected] = useState<boolean | null>(null)
  const [integrationLoading, setIntegrationLoading] = useState(true)

  // Upload modal state
  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const getHeaders = async (): Promise<Record<string, string>> => {
    const token = await getAuthToken()
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Tenant-Id': tenantId || '',
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
    return headers
  }

  const loadData = async () => {
    if (!tenantId) return

    if (!_cachedKBDocs) setIsLoading(true)
    setError(null)

    try {
      const headers = await getHeaders()

      // Load documents
      const docsResponse = await fetch(`${API_BASE}/v1/knowledge-base/documents`, { headers })
      if (docsResponse.ok) {
        const data = await docsResponse.json()
        const docs = data.documents || []
        setDocuments(docs)
        _cachedKBDocs = docs
      }

      // Load stats
      const statsResponse = await fetch(`${API_BASE}/v1/knowledge-base/stats`, { headers })
      if (statsResponse.ok) {
        const data = await statsResponse.json()
        setStats(data)
        _cachedKBStats = data
      }

      // Load categories
      const categoriesResponse = await fetch(`${API_BASE}/v1/knowledge-base/categories`, { headers })
      if (categoriesResponse.ok) {
        const data = await categoriesResponse.json()
        const cats = data.categories || []
        setCategories(cats)
        _cachedKBCategories = cats
      }
    } catch (err: any) {
      console.error('Failed to load knowledge base:', err)
      setError(err.message || 'Failed to load knowledge base')
    } finally {
      setIsLoading(false)
    }
  }

  // Check OpenAI integration on mount
  useEffect(() => {
    const checkIntegration = async () => {
      if (!tenantId) return

      setIntegrationLoading(true)
      try {
        const result = await checkKBIntegration()
        setOpenaiConnected(result.openai_connected)
      } catch (err) {
        console.error('Error checking integration:', err)
        setOpenaiConnected(false)
      } finally {
        setIntegrationLoading(false)
      }
    }

    if (tenantId && !tenantLoading) {
      checkIntegration()
    }
  }, [tenantId, tenantLoading])

  // Load data when integration check completes (regardless of result)
  useEffect(() => {
    if (tenantId && !tenantLoading && !integrationLoading) {
      // Load data even if OpenAI is not connected - we still need stats/documents from DynamoDB
      loadData()
    }
  }, [tenantId, tenantLoading, integrationLoading])

  const handleDelete = async (documentId: string) => {
    setIsDeleting(true)
    try {
      const headers = await getHeaders()
      const response = await fetch(`${API_BASE}/v1/knowledge-base/documents/${documentId}`, {
        method: 'DELETE',
        headers,
      })

      if (!response.ok) {
        throw new Error('Failed to delete document')
      }

      setDeleteConfirm(null)
      loadData()
    } catch (err: any) {
      console.error('Failed to delete document:', err)
    } finally {
      setIsDeleting(false)
    }
  }

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = !searchQuery ||
      doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.description.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCategory = !categoryFilter || doc.category === categoryFilter
    return matchesSearch && matchesCategory
  })

  // Show loading spinner only on first load when we have no cached data
  if (!_cachedKBDocs && (tenantLoading || (integrationLoading && isLoading))) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Knowledge Base</h1>
          <p className="text-gray-500 mt-1">Manage training documents for your AI agents</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={loadData}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            title="Refresh"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('documents')}
            className={`flex items-center space-x-2 py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'documents'
                ? 'border-yellow-500 text-yellow-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FileText className="w-4 h-4" />
            <span>Documents</span>
          </button>
          <button
            onClick={() => setActiveTab('chat')}
            className={`flex items-center space-x-2 py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'chat'
                ? 'border-yellow-500 text-yellow-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <MessageSquare className="w-4 h-4" />
            <span>Chat / Query</span>
          </button>
        </nav>
      </div>

      {/* Documents Tab Content */}
      {activeTab === 'documents' && (
        <>
          {/* OpenAI Integration Warning */}
          {!integrationLoading && !openaiConnected && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start space-x-3">
              <Settings className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-yellow-800">OpenAI Integration Required</p>
                <p className="text-sm text-yellow-700 mt-1">
                  To upload and process documents, you need to configure your OpenAI API key.
                </p>
                <Link
                  to="/integrations"
                  className="inline-flex items-center space-x-1 text-sm font-medium text-yellow-800 hover:text-yellow-900 mt-2"
                >
                  <span>Go to Integrations</span>
                  <ExternalLink className="w-3 h-3" />
                </Link>
              </div>
            </div>
          )}

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 border border-gray-100">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Documents</p>
                    <p className="text-xl font-bold text-gray-900">{stats.total_documents}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-gray-100">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Database className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Chunks</p>
                    <p className="text-xl font-bold text-gray-900">{stats.total_chunks || 0}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-gray-100">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <HardDrive className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Storage (S3)</p>
                    <p className="text-xl font-bold text-gray-900">{stats.total_size_mb} MB</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-gray-100">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <FolderOpen className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Categories</p>
                    <p className="text-xl font-bold text-gray-900">{Object.keys(stats.by_category || {}).length}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 appearance-none bg-white"
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
          </div>

          <KBDocumentExplorer
            documents={filteredDocuments}
            isLoading={isLoading}
            uploadsDisabled={!openaiConnected}
            onRefresh={loadData}
            onDeleteRequest={setDeleteConfirm}
          />
        </>
      )}

      {/* Chat Tab Content */}
      {activeTab === 'chat' && (
        <div className="space-y-6">
          {integrationLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
            </div>
          ) : !openaiConnected ? (
            <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Settings className="w-8 h-8 text-yellow-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                OpenAI Integration Required
              </h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                To use the Knowledge Base Chat, you need to configure your OpenAI API key first.
              </p>
              <Link
                to="/integrations"
                className="inline-flex items-center space-x-2 px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-medium rounded-lg transition-colors"
              >
                <span>Go to Integrations</span>
                <ExternalLink className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <KBChatInterface />
          )}
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Document</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this document? This will also remove it from the AI knowledge base.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg disabled:opacity-50 flex items-center space-x-2"
              >
                {isDeleting && <Loader2 className="w-4 h-4 animate-spin" />}
                <span>Delete</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
